import type { BlockUserRef, Person } from "../../api/types";
import type { SortState } from "../../components/SortableHeader";
import { displayName } from "../../lib/format";

// AchievementRow is one assignee's season scorecard: how many blocks they are
// assigned, how many of those are complete, and the completion percentage.
export interface AchievementRow {
  user: BlockUserRef;
  assigned: number;
  achieved: number;
  pct: number;
}

export interface AchievementTotals {
  assigned: number;
  achieved: number;
  pct: number;
}

export type AchievementSortKey = "person" | "assigned" | "achieved" | "pct";

export type AchievementSort = SortState<AchievementSortKey>;

// Multiply before dividing: (23 / 40) * 100 is 57.4999… in floating point and
// rounds down, while 2300 / 40 is exactly 57.5.
export function achievementPct(achieved: number, assigned: number): number {
  return assigned === 0 ? 0 : Math.round((achieved * 100) / assigned);
}

// Only people assigned at least one block get a row — owners and contributors
// with nothing assigned have no achievement to measure.
export function buildAchievementRows(people: Person[]): AchievementRow[] {
  return people
    .filter((person) => person.assigned_total > 0)
    .map((person) => ({
      user: person.user,
      assigned: person.assigned_total,
      achieved: person.assigned_complete,
      pct: achievementPct(person.assigned_complete, person.assigned_total)
    }));
}

export function achievementTotals(rows: AchievementRow[]): AchievementTotals {
  const assigned = rows.reduce((sum, row) => sum + row.assigned, 0);
  const achieved = rows.reduce((sum, row) => sum + row.achieved, 0);
  return { assigned, achieved, pct: achievementPct(achieved, assigned) };
}

function compareBy(key: AchievementSortKey, a: AchievementRow, b: AchievementRow): number {
  switch (key) {
    case "person":
      return displayName(a.user.name, a.user.email).localeCompare(displayName(b.user.name, b.user.email));
    case "assigned":
      return a.assigned - b.assigned;
    case "achieved":
      return a.achieved - b.achieved;
    case "pct":
      return a.pct - b.pct;
  }
}

// Ties fall back to the achievement ladder (pct, then assigned, then name) so
// equal values keep a predictable order whichever column is active.
export function sortAchievementRows(rows: AchievementRow[], sort: AchievementSort): AchievementRow[] {
  const dir = sort.dir === "asc" ? 1 : -1;
  return [...rows].sort(
    (a, b) =>
      compareBy(sort.key, a, b) * dir ||
      b.pct - a.pct ||
      b.assigned - a.assigned ||
      compareBy("person", a, b)
  );
}
